import React, { useState, useEffect, useContext, useRef } from 'react';
import './GroupUpdateModal.css';
import Modal from '../Modal/Modal';
import TextInput from '../TextInput/TextInput';
import CheckboxInput from '../CheckboxInput/CheckboxInput';
import SelectInput from '../SelectInput/SelectInput';
import UserCard from '../UserCard/UserCard';
import ActionButtons from '../ActionButtons/ActionButtons';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import BackendSearchSelect from '../BackendSearchSelect/BackendSearchSelect';
import convenienceApi from '../../common/api/convenience';
import baseApi from '../../common/api/base';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';
import logComponent from '../../common/logComponent';

export default function GroupUpdateModal({ group, updateGroup: propsUpdateGroup, groups, hide }) {
  const environmentContext = useContext(EnvironmentContext);
  const { hasToken } = environmentContext;
  const { activeUser: user } = environmentContext;
  const updatedGroupRef = useRef({
    name: null,
    canCorrespond: null,
    groupMembers: null,
    canViewSurveyResponses: null,
    checkInSurveyId: null
  });
  const modalRef = useRef(null);
  const firstTabRef = useRef(null);
  const lastTabRef = useRef(null);
  const actionButtons = useRef(null);

  const [currentMembersLoading, setCurrentMembersLoading] = useState(true);
  const [currentMembersError, setCurrentMembersError] = useState(false);
  const [currentMembers, setCurrentMembers] = useState([]);
  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [fields, setFields] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [status, setStatus] = useState('initial');
  const [totalCorrespondents, setTotalCorrespondents] = useState(0);
  const [totalImpersonators, setTotalImpersonators] = useState(0);
  const [modalShouldExit, setModalShouldExit] = useState(false);
  const [needUpdateTotals, setNeedUpdateTotals] = useState(false);
  const formIsInvalid = isValid === false;
  const { facilityId, statusSolutionsId, corporationId } = environmentContext.activeUser;
  const [surveys, setSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [surveysError, setSurveysError] = useState(false);
  const [checkInSurveyId, setCheckInSurveyId] = useState(0);
  const [checkInSurveyIdLoading, setCheckInSurveyIdLoading] = useState(true);
  const [checkInSurveyIdError, setCheckInSurveyIdError] = useState(false);
  const isLoading = useRef(false);

  if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal render'); }

  const setUpdatedGroupRef = (el) => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal setUpdatedGroupRef'); }
    updatedGroupRef.current = el;
  }

  const setModalRef = (el) => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal setModalRef'); }
    modalRef.current = el;
  }

  const setFirstTabRef = (el) => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal setFirstTabRef'); }
    firstTabRef.current = el;
  }

  const setLastTabRef = (el) => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal setLastTabRef'); }
    lastTabRef.current = el;
  }


  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal onMount'); }
    if (!hasToken) return;

    loadData(group);
  }, []);

  useEffect(() => {
    if (!hasToken) return;

    loadData();
  }, [environmentContext.hasToken]);

  useEffect(() => {
    if (!hasToken) return;

    // handle submission failure
    if (status === 'failure') {
      modalRef.current.scrollTop = 0;
    }
  }, [status]);

  useEffect(() => {
    if (needUpdateTotals === true) updateTotals();
  }, [needUpdateTotals]);

  const loadData = (group) => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal loadData'); }
    if (!hasToken || !group || isLoading.current) return;
    isLoading.current = true;

    setCurrentMembersLoading(true);
    setCurrentMembersError(false);

    // get users related to group
    convenienceApi.getGroupMembers(group)
      .then(result => {
        setCurrentMembers(result);
        setCurrentMembersLoading(false);
        setCurrentMembersError(false);
      })
      .catch(error => {
        setCurrentMembersLoading(false);
        setCurrentMembersError(true);
      });
    if (facilityId) {
      setSurveys(null);
      setSurveysLoading(true);
      setSurveysError(false);
      setCheckInSurveyId(0);
      setCheckInSurveyIdLoading(true);
      setCheckInSurveyIdError(false);


      baseApi.Survey.GetAllCheckInSurveysForFacility(facilityId)
        .then(
          result => {
            let defaultChoice = [{ key: '', value: '' }];
            let availableSurveys = result.data.map(survey => {
              return {
                key: survey.surveyName + ' (' + survey.ownerName + ')', 
                value: survey.surveyId
              };
            });
            let newSurveys = defaultChoice.concat(availableSurveys);
            setSurveys(newSurveys);
            setSurveysLoading(false);
            setSurveysError(false);

          },
          error => {
            setSurveys(null);
            setSurveysLoading(false);
            setSurveysError(true);
          }
      );

      baseApi.Group.GetGroupCheckInSurvey(group.groupId)
        .then(
          result => {
            let checkInSurveyForGroup = result.data;
            setCheckInSurveyId(checkInSurveyForGroup.surveyId);
            
            setCheckInSurveyIdLoading(false);
            setCheckInSurveyIdError(false);

          },
          error => {
            setCheckInSurveyId(0);
            setCheckInSurveyIdLoading(false);
            setCheckInSurveyIdError(true);
         }
        );
        isLoading.current = false;
    }


  }

  const showValidationErrors = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal showValidationErrors'); }
    setShowErrors(true);
  }

  const hideValidationErrors = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal hideValidationErrors'); }
    setShowErrors(false);
  }

  const validateField = (field) => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal validateField'); }
    let thisUpdatedGroup = Object.assign({}, updatedGroupRef.current);
    thisUpdatedGroup[lowerCaseFirstLetter(field.name)] = field;
    setUpdatedGroupRef(thisUpdatedGroup);
    if (field.name === 'GroupMembers') {
      setNeedUpdateTotals(true);
    }
  }

  const submit = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal submit'); }
    const { facilityId, statusSolutionsId, corporationId } = environmentContext.activeUser;
    // check all necessary fields
    let requiredFieldList = ['name', 'canCorrespond', 'groupMembers'];

    if (facilityId == null) {
      requiredFieldList = ['name', 'groupMembers'];
    }

    let isValid = requiredFieldList.reduce((prev, curr) => prev && updatedGroupRef.current[curr] && updatedGroupRef.current[curr].isValid, true);

    if (!isValid) {
      handleInvalidSubmission();
    } else {
      handleValidSubmission();
    }
  }

  const handleValidSubmission = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal handleValidSubmission'); }
    hideValidationErrors();
    updateGroup();
    setIsValid(true);
  }

  const handleInvalidSubmission = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal handleInvalidSubmission'); }
    showValidationErrors();
    modalRef.current.scrollTop = 0;
    if (firstTabRef.current) firstTabRef.current.focus();

    setIsValid(false);
  }

  const updateGroup = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal updateGroup'); }
    let originalGroup = { ...group };
    let groupToSubmit = {};
    const { facilityId } = environmentContext.activeUser;
    setStatus('pending');
    let checkInSurveyChanged = (group.checkInSurveyId != null && updatedGroupRef.current.checkInSurveyId == null) || (group.checkInSurveyId == null && updatedGroupRef.current.checkInSurveyId != null) || (group.checkInSurveyId && updatedGroupRef.current.checkInSurveyId && group.checkInSurveyId !== updatedGroupRef.checkInSurveyId);


    for (let prop in updatedGroupRef.current) {
      if (prop == 'canCorrespond' && facilityId == null) {
        groupToSubmit[prop] = false;
      } else if (prop == 'canViewSurveyResponses' && facilityId == null) {
        groupToSubmit[prop] = false;
      } else if (prop == 'checkInSurveyId' && facilityId == null) {
        groupToSubmit[prop] = 0;
      } else {
        groupToSubmit[prop] = updatedGroupRef.current[prop].value;
      }
    }

    // Add necessary fields 
    groupToSubmit.facilityId = originalGroup.facilityId;
    groupToSubmit.corporationId = originalGroup.corporationId;
    groupToSubmit.statusSolutionsId = originalGroup.statusSolutionsId;
    groupToSubmit.groupId = originalGroup.groupId;
    groupToSubmit.canViewSurveyResponses = groupToSubmit.canViewSurveyResponses == null ? false : groupToSubmit.canViewSurveyResponses;

    originalGroup.groupMembers = currentMembers;
    originalGroup.checkInSurveyId = checkInSurveyId;

    convenienceApi.updateGroup(groupToSubmit, originalGroup)
      .then(result => {
        setStatus('success');
        propsUpdateGroup(result);

        // If user has been added to or removed from this group
        if ((originalGroup.groupMembers.some(originalMember => originalMember.userId === user.userId) &&
          !groupToSubmit.groupMembers.some(newMember => newMember.userId === user.userId)) ||
          (!originalGroup.groupMembers.some(originalMember => originalMember.userId === user.userId) &&
            groupToSubmit.groupMembers.some(newMember => newMember.userId === user.userId))) {
          environmentContext.setShouldUpdateModules(true);
          if (checkInSurveyChanged) {
            environmentContext.setShouldUpdateCheckInSurvey(true);

          }
        } else {
          if (groupToSubmit.groupMembers.some(newMember => newMember.userId === user.userId)) {

            environmentContext.setShouldUpdateCheckInSurvey(true);
          }

        }

        startModalExit();

      })
      .catch(error => {
        setStatus('failure');
      });
  }

  const updateTotals = (users) => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal updateTotals'); }
    updateTotalCorrespondents(users);
    updateTotalImpersonators(users);
  }

  const updateTotalCorrespondents = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal updateTotalCorrespondents'); }
    const members = updatedGroupRef.current.groupMembers.value;

    let count = members.reduce((prev, curr) => {
      return (curr.canCorrespond ? prev + 1 : prev);
    }, 0);

    setTotalCorrespondents(count);
  }

  const updateTotalImpersonators = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal updateTotalImpersonators'); }
    const members = updatedGroupRef.current.groupMembers.value;

    let count = members.reduce((prev, curr) => {
      return (curr.canImpersonate ? prev + 1 : prev);
    }, 0);
    setTotalImpersonators(count);
  }

  const startModalExit = () => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal startModalExit'); }
    setModalShouldExit(true);
  }

  const handleShiftTab = (e) => {
    if (logComponent('GroupUpdateModal')) { console.debug('GroupUpdateModal handleShiftTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || !shiftKey) return;

    e.preventDefault();
    lastTabRef.current.focus();
  }

  return (

    <Modal isVisible={true} shouldExit={modalShouldExit} hide={hide} ref={setModalRef} remainOnBackdropClick={true}>
      <div className="Group-Update-Modal">
        <h3 className="Group-Update-Modal__heading">Modify group</h3>
        <p className={'Group-Update-Modal__page-error' + (showErrors ? ' visible' : '')}>There were some errors. Please double check the information and resubmit.</p>
        <p className={'Group-Update-Modal__page-error' + (status === 'failure' ? ' visible' : '')}>Sorry, we were unable to complete the action.  Group was not updated.  Please try again.</p>
        <form className="Group-Update-Modal__form">
          <TextInput
            initialValue={group.name}
            label="Group name"
            name="name"
            rules={{
              required: true,
              notIn: groups
                .filter(otherGroup => otherGroup.groupId !== group.groupId)
                .map(otherGroup => otherGroup.name)
            }}
            errorMessages={{
              required: 'Please provide a name for the group',
              notIn: 'A group already exists with that name, please choose a unique name.'
            }}
            showError={formIsInvalid}
            validate={validating}
            onValidate={validateField}
            ref={setFirstTabRef}
            onKeyDown={handleShiftTab} />
          {
            facilityId &&
            <CheckboxInput
              initialValue={group.canCorrespond}
              label="Can correspond with individuals' family?"
              name="canCorrespond"
              instructions={totalCorrespondents > 0 ? `Note: ${totalCorrespondents} member${totalCorrespondents > 1 ? 's' : ''} of this group ${totalCorrespondents > 1 ? 'are' : 'is'} enabled for correspondence manually` : ''}
              explanation="Individuals in this group may correspond with individuals' family"
              validate={validating}
              onValidate={validateField} />
          }
          {
            (statusSolutionsId || corporationId) &&
            <CheckboxInput
              initialValue={group.canImpersonate}
              label="Can Mimic other MIMI users?"
              name="canImpersonate"
              instructions={totalImpersonators > 0 ? `Note: ${totalImpersonators} member${totalImpersonators > 1 ? 's' : ''} of this group ${totalImpersonators > 1 ? 'are' : 'is'} enabled for mimicking manually` : ''}
              explanation="Individuals in this group may mimic other MIMI users associated with their organization"
              validate={validating}
              onValidate={validateField} />
          }
          {
            facilityId &&
            <CheckboxInput
              label="View SurveyResponses override"
              name="canViewSurveyResponses"
              instructions={totalImpersonators > 0 ? `Note: ${totalImpersonators} member${totalImpersonators > 1 ? 's' : ''} of this group ${totalImpersonators > 1 ? 'are' : 'is'} enabled for viewing survey responses manually` : ''}
              explanation="Individuals in this group may view survey responses."
              initialValue={group.canViewSurveyResponses}
              validate={validating}
              onValidate={validateField} />

          }
          {
            facilityId &&
            <SelectInput
              label="Check-In survey"
              name="checkInSurveyId"
              initialValue={checkInSurveyId}
              items={surveys}
              errorMessage="Please select a check-in survey for this group.  If not is selected, it will use the default survey"
              showError={formIsInvalid}
              rules={null}
              validate={validating}
              onValidate={validateField}
            />

          }
          <BackendSearchSelect
            label="Select group members"
            itemKeyField="userId"
            itemPluralTerm="users"
            searchFunction={facilityId != null ? convenienceApi.searchFacilityUsers : corporationId != null ? convenienceApi.searchCorporationUsers : convenienceApi.searchStatusSolutionsUsers}
            itemComponent={UserCard}
            itemComponentPropName="user"
            selectedItems={currentMembers}
            selectedItemsLoading={currentMembersLoading}
            selectedItemsError={currentMembersError}
            name="groupMembers"
            validate={validating}
            onValidate={validateField}
          />
          <ActionButtons
            ref={actionButtons}
            mainAction={submit}
            mainActionText="Save changes"
            cancel={startModalExit}
            cancelText="Cancel"
            currentStatus={status} />
        </form>
      </div>
    </Modal>
  );
}